import type {
  AgingStockQueryDTO,
  DeadStockQueryDTO,
  MarketingRoiQueryDTO,
  ProductPerformanceQueryDTO,
  ProfitByProductQueryDTO,
  ProfitCampaignSummaryQueryDTO,
  ProfitReportQueryDTO,
  ReportDateRangeDTO,
  ReportProductFilterDTO,
  SalesFrequencyQueryDTO,
  ShippingPerformanceQueryDTO,
  StockMovementsQueryDTO,
  StockValuationQueryDTO,
  UpdateDeadStockSettingsDTO,
  CustomerReportQueryDTO,
  CustomerTopQueryDTO,
  CustomerGeoDistrictQueryDTO,
  CustomerWatchlistQueryDTO,
  CustomerExportQueryDTO,
  CustomerProductAffinityQueryDTO,
  CustomerCohortsQueryDTO,
} from "@/modules/reports/domain/contracts";
import type {
  AgingStockRowDTO,
  DeadStockResultDTO,
  DeadStockSettingsResponseDTO,
  MarketingRoiResultDTO,
  ProductPerformanceRowDTO,
  ProfitByDayRowDTO,
  ProfitByProductRowDTO,
  ProfitCampaignSummaryRowDTO,
  ProfitSummaryDTO,
  SalesByDayRowDTO,
  SalesFrequencyRowDTO,
  SalesSummaryDTO,
  ShippingPerformanceResultDTO,
  StockMovementsResult,
  StockValuationDTO,
  CustomerReportSummaryDTO,
  CustomerTopResultDTO,
  CustomerGeoResultDTO,
  CustomerWatchlistResultDTO,
  CustomerRiskTierSummaryDTO,
  CustomerProductAffinityResultDTO,
  CustomerCohortsResultDTO,
} from "@/modules/reports/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type ReportDateRangeQuery = ReportDateRangeDTO;
export type ProfitReportQuery = ProfitReportQueryDTO;

function appendProductFilter(url: URL, query: ReportProductFilterDTO) {
  if (query.productId) url.searchParams.set("productId", query.productId);
  if (query.variantId) url.searchParams.set("variantId", query.variantId);
}

function appendMarketingFilters(
  url: URL,
  query: Pick<
    ProfitReportQueryDTO,
    "platform" | "campaignName" | "mappedProductId" | "mappedCategoryId"
  >,
) {
  if (query.platform) url.searchParams.set("platform", query.platform);
  if (query.campaignName)
    url.searchParams.set("campaignName", query.campaignName);
  if (query.mappedProductId) {
    url.searchParams.set("mappedProductId", query.mappedProductId);
  }
  if (query.mappedCategoryId) {
    url.searchParams.set("mappedCategoryId", query.mappedCategoryId);
  }
}

function buildRangeUrl(path: string, query: ReportDateRangeQuery) {
  const url = new URL(`${config.apiUrl}/reports/${path}`);
  url.searchParams.set("from", query.from);
  url.searchParams.set("to", query.to);
  if (query.basis) url.searchParams.set("basis", query.basis);
  appendProductFilter(url, query);
  appendMarketingFilters(url, query);
  return url.toString();
}

function buildProfitUrl(path: string, query: ProfitReportQuery) {
  const url = new URL(`${config.apiUrl}/reports/${path}`);
  url.searchParams.set("from", query.from);
  url.searchParams.set("to", query.to);
  if (query.basis) url.searchParams.set("basis", query.basis);
  appendProductFilter(url, query);
  appendMarketingFilters(url, query);
  return url.toString();
}

function buildCustomerCohortsUrl(query: CustomerCohortsQueryDTO) {
  const url = new URL(`${config.apiUrl}/reports/customers/cohorts`);
  if (query.months != null) url.searchParams.set("months", String(query.months));
  if (query.maxOffset != null) {
    url.searchParams.set("maxOffset", String(query.maxOffset));
  }
  if (query.province) url.searchParams.set("province", query.province);
  if (query.district) url.searchParams.set("district", query.district);
  return url.toString();
}

function buildCustomerReportUrl(path: string, query: CustomerReportQueryDTO) {
  const url = new URL(`${config.apiUrl}/reports/customers/${path}`);
  url.searchParams.set("from", query.from);
  url.searchParams.set("to", query.to);
  if (query.basis) url.searchParams.set("basis", query.basis);
  if (query.province) url.searchParams.set("province", query.province);
  if (query.district) url.searchParams.set("district", query.district);
  return url.toString();
}

async function downloadCsvFromUrl(url: string, fallbackFilename: string) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error ?? "ດາວໂຫລດລົ້ມເຫຼວ");
  }
  const blob = await res.blob();
  const filename =
    res.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1] ??
    fallbackFilename;
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export const reportsApi = {
  async salesSummary(query: ReportDateRangeQuery): Promise<SalesSummaryDTO> {
    return fetcher.get(buildRangeUrl("sales-summary", query));
  },
  async customJobsSummary(
    query: ReportDateRangeQuery,
  ): Promise<import("@/modules/reports/domain/repo/custom-jobs-summary").CustomJobsSummaryDTO> {
    return fetcher.get(buildRangeUrl("custom-jobs-summary", query));
  },
  async salesByDay(query: ReportDateRangeQuery): Promise<SalesByDayRowDTO[]> {
    return fetcher.get(buildRangeUrl("sales-by-day", query));
  },
  async productPerformance(
    query: ProductPerformanceQueryDTO,
  ): Promise<ProductPerformanceRowDTO[]> {
    const url = new URL(buildRangeUrl("product-performance", query));
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.sort) url.searchParams.set("sort", query.sort);
    return fetcher.get(url.toString());
  },
  async agingStock(query: AgingStockQueryDTO): Promise<AgingStockRowDTO[]> {
    const url = new URL(`${config.apiUrl}/reports/aging-stock`);
    url.searchParams.set("minDays", String(query.minDays ?? 30));
    url.searchParams.set("limit", String(query.limit ?? 20));
    appendProductFilter(url, query);
    return fetcher.get(url.toString());
  },
  async deadStock(query?: DeadStockQueryDTO): Promise<DeadStockResultDTO> {
    const url = new URL(`${config.apiUrl}/reports/dead-stock`);
    if (query?.lookbackDays) {
      url.searchParams.set("lookbackDays", String(query.lookbackDays));
    }
    if (query?.minDays) url.searchParams.set("minDays", String(query.minDays));
    if (query?.limit) url.searchParams.set("limit", String(query.limit));
    if (query) appendProductFilter(url, query);
    return fetcher.get(url.toString());
  },
  async deadStockSettings(): Promise<DeadStockSettingsResponseDTO> {
    return fetcher.get(`${config.apiUrl}/reports/dead-stock/settings`);
  },
  async updateDeadStockSettings(
    input: UpdateDeadStockSettingsDTO,
  ): Promise<DeadStockSettingsResponseDTO> {
    return fetcher.patch(`${config.apiUrl}/reports/dead-stock/settings`, input);
  },
  async downloadDeadStockCsv(query?: DeadStockQueryDTO): Promise<void> {
    const url = new URL(`${config.apiUrl}/reports/dead-stock/export`);
    if (query?.lookbackDays) {
      url.searchParams.set("lookbackDays", String(query.lookbackDays));
    }
    if (query?.minDays) url.searchParams.set("minDays", String(query.minDays));
    if (query?.limit) url.searchParams.set("limit", String(query.limit));
    if (query) appendProductFilter(url, query);
    await downloadCsvFromUrl(url.toString(), "dead-stock.csv");
  },
  async downloadAgingStockCsv(query: AgingStockQueryDTO): Promise<void> {
    const url = new URL(`${config.apiUrl}/reports/aging-stock/export`);
    url.searchParams.set("minDays", String(query.minDays ?? 30));
    url.searchParams.set("limit", String(query.limit ?? 20));
    appendProductFilter(url, query);
    await downloadCsvFromUrl(url.toString(), "aging-stock.csv");
  },
  async downloadProductPerformanceCsv(
    query: ProductPerformanceQueryDTO,
  ): Promise<void> {
    const url = new URL(buildRangeUrl("product-performance/export", query));
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.sort) url.searchParams.set("sort", query.sort);
    await downloadCsvFromUrl(url.toString(), "products.csv");
  },
  async downloadSalesFrequencyCsv(
    query: SalesFrequencyQueryDTO,
  ): Promise<void> {
    const url = new URL(buildRangeUrl("sales-frequency/export", query));
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.sort) url.searchParams.set("sort", query.sort);
    await downloadCsvFromUrl(url.toString(), "sales-frequency.csv");
  },
  async downloadShippingCsv(query: ShippingPerformanceQueryDTO): Promise<void> {
    const url = new URL(buildRangeUrl("shipping-performance/export", query));
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    await downloadCsvFromUrl(url.toString(), "shipping.csv");
  },
  async salesFrequency(
    query: SalesFrequencyQueryDTO,
  ): Promise<SalesFrequencyRowDTO[]> {
    const url = new URL(buildRangeUrl("sales-frequency", query));
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.sort) url.searchParams.set("sort", query.sort);
    return fetcher.get(url.toString());
  },
  async stockMovements(
    query: StockMovementsQueryDTO,
  ): Promise<StockMovementsResult> {
    const url = new URL(`${config.apiUrl}/reports/stock-movements`);
    url.searchParams.set("from", query.from);
    url.searchParams.set("to", query.to);
    appendProductFilter(url, query);
    if (query.reason) url.searchParams.set("reason", query.reason);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    return fetcher.get(url.toString());
  },
  async profitSummary(query: ProfitReportQuery): Promise<ProfitSummaryDTO> {
    return fetcher.get(buildProfitUrl("profit-summary", query));
  },
  async profitByDay(query: ProfitReportQuery): Promise<ProfitByDayRowDTO[]> {
    return fetcher.get(buildProfitUrl("profit-by-day", query));
  },
  async profitByProduct(
    query: ProfitByProductQueryDTO,
  ): Promise<ProfitByProductRowDTO[]> {
    const url = new URL(buildProfitUrl("profit-by-product", query));
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    return fetcher.get(url.toString());
  },
  async profitCampaigns(
    query: ProfitCampaignSummaryQueryDTO,
  ): Promise<ProfitCampaignSummaryRowDTO[]> {
    const url = new URL(buildProfitUrl("profit-campaigns", query));
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    return fetcher.get(url.toString());
  },
  async marketingRoi(
    query: MarketingRoiQueryDTO,
  ): Promise<MarketingRoiResultDTO> {
    const url = new URL(buildProfitUrl("marketing-roi", query));
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    return fetcher.get(url.toString());
  },
  async shippingPerformance(
    query: ShippingPerformanceQueryDTO,
  ): Promise<ShippingPerformanceResultDTO> {
    const url = new URL(buildRangeUrl("shipping-performance", query));
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    return fetcher.get(url.toString());
  },
  async customerReportSummary(
    query: CustomerReportQueryDTO,
  ): Promise<CustomerReportSummaryDTO> {
    return fetcher.get(buildCustomerReportUrl("summary", query));
  },
  async customerReportTop(
    query: CustomerTopQueryDTO,
  ): Promise<CustomerTopResultDTO> {
    const url = new URL(buildCustomerReportUrl("top", query));
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.sort) url.searchParams.set("sort", query.sort);
    return fetcher.get(url.toString());
  },
  async customerReportByProvince(
    query: CustomerReportQueryDTO,
  ): Promise<CustomerGeoResultDTO> {
    return fetcher.get(buildCustomerReportUrl("by-province", query));
  },
  async customerReportByDistrict(
    query: CustomerGeoDistrictQueryDTO,
  ): Promise<CustomerGeoResultDTO> {
    return fetcher.get(buildCustomerReportUrl("by-district", query));
  },
  async customerWatchlist(
    query: CustomerWatchlistQueryDTO,
  ): Promise<CustomerWatchlistResultDTO> {
    const url = new URL(buildCustomerReportUrl("watchlists", query));
    url.searchParams.set("kind", query.kind);
    if (query.minCodReturns != null) {
      url.searchParams.set("minCodReturns", String(query.minCodReturns));
    }
    if (query.codScope) url.searchParams.set("codScope", query.codScope);
    if (query.inactiveDays != null) {
      url.searchParams.set("inactiveDays", String(query.inactiveDays));
    }
    if (query.highNewThresholdLak != null) {
      url.searchParams.set(
        "highNewThresholdLak",
        String(query.highNewThresholdLak),
      );
    }
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    return fetcher.get(url.toString());
  },
  async customerRiskSummary(
    query: CustomerReportQueryDTO,
  ): Promise<CustomerRiskTierSummaryDTO> {
    return fetcher.get(buildCustomerReportUrl("risk-summary", query));
  },
  async customerProductAffinity(
    query: CustomerProductAffinityQueryDTO,
  ): Promise<CustomerProductAffinityResultDTO> {
    const url = new URL(buildCustomerReportUrl("product-affinity", query));
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    return fetcher.get(url.toString());
  },
  async customerCohorts(
    query: CustomerCohortsQueryDTO,
  ): Promise<CustomerCohortsResultDTO> {
    return fetcher.get(buildCustomerCohortsUrl(query));
  },
  async downloadCustomerCsv(query: CustomerExportQueryDTO): Promise<void> {
    const url = new URL(buildCustomerReportUrl("export", query));
    if (query.exportSection) {
      url.searchParams.set("exportSection", query.exportSection);
    }
    if (query.watchKind) url.searchParams.set("watchKind", query.watchKind);
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    await downloadCsvFromUrl(
      url.toString(),
      `customers-${query.from.slice(0, 10)}.csv`,
    );
  },
  async downloadOrdersCsv(query: ReportDateRangeQuery): Promise<void> {
    await downloadCsvFromUrl(
      buildRangeUrl("orders-export", query),
      "orders.csv",
    );
  },
  async downloadProfitCsv(query: ProfitReportQuery): Promise<void> {
    await downloadCsvFromUrl(
      buildProfitUrl("profit-export", query),
      "profit.csv",
    );
  },
  async stockValuation(
    query: StockValuationQueryDTO,
  ): Promise<StockValuationDTO> {
    const url = new URL(`${config.apiUrl}/reports/stock-valuation`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    appendProductFilter(url, query);
    return fetcher.get(url.toString());
  },
  async downloadStockValuationCsv(
    query: StockValuationQueryDTO,
  ): Promise<void> {
    const url = new URL(`${config.apiUrl}/reports/stock-valuation/export`);
    url.searchParams.set("limit", String(query.limit ?? 200));
    appendProductFilter(url, query);
    await downloadCsvFromUrl(url.toString(), "stock-valuation.csv");
  },
};
